import type { EdgeBar } from '../types/chart'
import { isEarlyCloseDay } from './marketCalendar'

export const REGULAR_OPEN_MINUTES = 9 * 60 + 30 // 09:30 → 570
const REGULAR_CLOSE_MINUTES = 16 * 60 // 16:00 → 960
const EARLY_CLOSE_MINUTES = 13 * 60 // 13:00 → 780

/** Extended session opens 04:00 ET → 240. */
export const EXTENDED_OPEN_MINUTES = 4 * 60
/** Extended session closes 20:00 ET → 1200 on a normal day. */
const EXTENDED_CLOSE_MINUTES = 20 * 60
/** On a 13:00 half day, after-hours stops at 17:00 → 1020 (see marketCalendar). */
const EARLY_EXTENDED_CLOSE_MINUTES = 17 * 60

/**
 * Which part of the trading day a timestamp falls in.
 *
 * `closed` covers both the overnight gap and non-trading days — this classifier
 * only reads the clock, so a Sunday 10:00 still reads `regular`. Callers that
 * care use `isMarketHoliday` / the bar data itself.
 */
export type SessionSegment = 'pre' | 'regular' | 'after' | 'closed'

export interface SessionClock {
  /** Minutes since exchange-local midnight. */
  minutes: number
  /** Exchange-local calendar date, 'YYYY-MM-DD'. */
  dateStr: string
}

// One formatter per timezone. `isRegularSession` used to build a fresh
// Intl.DateTimeFormat on every call, and DataLayer runs it once per bar.
const clockDtfCache = new Map<string, Intl.DateTimeFormat>()

function clockDtf(timezone: string): Intl.DateTimeFormat {
  let dtf = clockDtfCache.get(timezone)
  if (!dtf) {
    // en-CA yields 'YYYY-MM-DD' directly, as sessionDay.ts relies on too.
    dtf = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
    clockDtfCache.set(timezone, dtf)
  }
  return dtf
}

/**
 * Bar timestamps are immutable, so a resolved clock never changes.
 *
 * Nested per timezone with NUMERIC keys, rather than one flat map keyed by
 * `tz|timestamp`. Two reasons: a numeric-keyed Map lookup is roughly an order of
 * magnitude cheaper than building and hashing a template string, and this runs
 * once per bar per draw; and the budget is then PER timezone, so several charts
 * sharing one exchange do not divide a single allowance between them.
 *
 * Bounded because a long lookback on a fine feed would otherwise grow without
 * limit. On overflow that timezone's map is dropped wholesale rather than evicted
 * key by key — the access pattern is a forward sweep over bars, so a cleared map
 * simply refills as the sweep continues, costing one recompute per bar.
 */
const CLOCK_CACHE_LIMIT = 200_000
const clockCache = new Map<string, Map<number, SessionClock>>()

/** Exchange-local minute-of-day and date for a timestamp. */
export function sessionClock(timestamp: number, timezone: string): SessionClock {
  let perTz = clockCache.get(timezone)
  if (!perTz) {
    perTz = new Map<number, SessionClock>()
    clockCache.set(timezone, perTz)
  }
  const hit = perTz.get(timestamp)
  if (hit) return hit

  const parts = clockDtf(timezone).formatToParts(new Date(timestamp))
  let year = ''
  let month = ''
  let day = ''
  let hour = 0
  let minute = 0
  for (const p of parts) {
    if (p.type === 'year') year = p.value
    else if (p.type === 'month') month = p.value
    else if (p.type === 'day') day = p.value
    else if (p.type === 'hour') hour = Number(p.value)
    else if (p.type === 'minute') minute = Number(p.value)
  }

  const resolved: SessionClock = {
    minutes: hour * 60 + minute,
    dateStr: `${year}-${month}-${day}`,
  }
  if (perTz.size >= CLOCK_CACHE_LIMIT) perTz.clear()
  perTz.set(timestamp, resolved)
  return resolved
}

/** Regular-session close for an exchange-local date, in minutes past midnight.
 *  Exported so session-day bucketing shares the same boundary (early closes
 *  included) instead of hardcoding 16:00. */
export function regularCloseMinutesFor(dateStr: string): number {
  return isEarlyCloseDay(dateStr) ? EARLY_CLOSE_MINUTES : REGULAR_CLOSE_MINUTES
}

/**
 * Classify a timestamp into pre-market / regular / after-hours / closed.
 *
 * Shares its boundaries with `isRegularSession` deliberately: a forked copy that
 * hardcoded 16:00 would call 13:30 "regular" on the ~3 NYSE half days a year.
 * On those days the regular session ends at 13:00 and after-hours runs to 17:00.
 */
export function classifySessionSegment(timestamp: number, timezone: string): SessionSegment {
  const { minutes, dateStr } = sessionClock(timestamp, timezone)
  if (minutes < EXTENDED_OPEN_MINUTES) return 'closed'
  if (minutes < REGULAR_OPEN_MINUTES) return 'pre'

  const early = isEarlyCloseDay(dateStr)
  if (minutes < (early ? EARLY_CLOSE_MINUTES : REGULAR_CLOSE_MINUTES)) return 'regular'
  return minutes < (early ? EARLY_EXTENDED_CLOSE_MINUTES : EXTENDED_CLOSE_MINUTES)
    ? 'after'
    : 'closed'
}

/**
 * Returns true if the given timestamp falls within US regular trading hours
 * in the specified timezone. Regular session is 09:30 – 16:00 ET on normal
 * trading days, but ends at 13:00 ET on the ~3 NYSE early-close days per
 * year (Black Friday, July 3 when applicable, Christmas Eve when
 * applicable). The set of those dates lives in `marketCalendar.ts`.
 */
export function isRegularSession(timestamp: number, timezone: string): boolean {
  const { minutes, dateStr } = sessionClock(timestamp, timezone)
  if (minutes < REGULAR_OPEN_MINUTES) return false
  return minutes < regularCloseMinutesFor(dateStr)
}

/**
 * Creates a bar filter function that keeps only regular-session bars.
 */
export function createRegularSessionFilter(timezone: string): (bar: EdgeBar) => boolean {
  return (bar) => isRegularSession(bar.timestamp, timezone)
}
